import { tableNodes } from 'prosemirror-tables';

const Nodelements = {
  paragraph: ['p', 0],
  blockquote: ['blockquote', 0],
  horizontal_rule: ['hr'],
  pre: ['pre', ['code', 0]],
  br: ['br'],
  div: ['div', 0],
};

const tableNodesSpec = tableNodes({
  tableGroup: 'block',
  cellContent: 'block+',
  cellAttributes: {
    background: {
      default: null,
      getFromDOM(dom) {
        return dom.style.backgroundColor || null
      },
      setDOMAttr(value, attrs) {
        if (value) {
          attrs.style = (attrs.style || "") + `background-color: ${value};`
        }
      }
    }
  }
});

// :: Object
// [Specs](#model.NodeSpec) for the nodes defined in this schema.
const nodes = {
  // :: NodeSpec The top level document node.
  doc: {
    content: 'block+'
  },

  // :: NodeSpec A plain paragraph textblock. Represented in the DOM
  // as a `<p>` element.
  paragraph: {
    content: 'inline*',
    group: 'block',
    attrs: {
      style: { default: null },
      classes: { default: null },
    },
    parseDOM: [
      {
        tag: 'p',
        getAttrs(dom) {
          return {
            style: dom.getAttribute('style'),
            classes: dom.getAttribute('class')
          };
        }
      }
    ],
    toDOM(node) {
      let { style, classes } = node.attrs;
      return ['p', { style, class: classes }, 0];
    }
  },

  // :: NodeSpec A blockquote (`<blockquote>`) wrapping one or more blocks.
  blockquote: {
    content: 'block+',
    group: 'block',
    defining: true,
    parseDOM: [{tag: 'blockquote'}],
    toDOM() { return Nodelements.paragraph }
  },

  // :: NodeSpec A horizontal rule (`<hr>`).
  horizontal_rule: {
    group: 'block',
    draggable: true,
    attrs: {
      style: { default: 'border-bottom:solid 1px #000; width: 90%;' },
      dryeditor: { default: 'horizontalrule' },
    },
    parseDOM: [
      {
        tag: 'div[data-dry=horizontalrule]',
        getAttrs(dom) {
          return {
            style: dom.getAttribute('style'),
            dryeditor: dom.getAttribute('data-dry')
          };
        }
      },
      {
        tag: 'hr'
      }
    ],
    toDOM(node) {
      let { style, dryeditor } = node.attrs;
      return ['div', { style, dryeditor }, 0];
    }
  },

  // :: NodeSpec A heading textblock, with a `level` attribute that
  // should hold the number 1 to 6. Parsed and serialized as `<h1>` to
  // `<h6>` elements.
  heading: {
    attrs: {
      level: { default: 1 },
      style: { default: null },
      classes: { default: null },
    },
    content: 'inline*',
    group: 'block',
    defining: true,
    parseDOM: [
      {
        tag: 'h1',
        attrs: {level: 1},
        getAttrs(dom) {
          return {
            style: dom.getAttribute('style'),
            classes: dom.getAttribute('class')
          };
        }
      },
      {
        tag: 'h2',
        attrs: {level: 2},
        getAttrs(dom) {
          return {
            style: dom.getAttribute('style'),
            classes: dom.getAttribute('class')
          };
        }
      },
      {
        tag: 'h3',
        attrs: {level: 3},
        getAttrs(dom) {
          return {
            style: dom.getAttribute('style'),
            classes: dom.getAttribute('class')
          };
        }
      },
      {
        tag: 'h4',
        attrs: {level: 4},
        getAttrs(dom) {
          return {
            style: dom.getAttribute('style'),
            classes: dom.getAttribute('class')
          };
        }
      },
      {
        tag: 'h5',
        attrs: {level: 5},
        getAttrs(dom) {
          return {
            style: dom.getAttribute('style'),
            classes: dom.getAttribute('class')
          };
        }
      },
      {
        tag: 'h6',
        attrs: {level: 6},
        getAttrs(dom) {
          return {
            style: dom.getAttribute('style'),
            classes: dom.getAttribute('class')
          };
        }
      }
    ],
    toDOM(node) {
      const { style, classes } = node.attrs;
      return ['h' + node.attrs.level, { style, class: classes }, 0]
    }
  },

  // :: NodeSpec A code listing. Disallows marks or non-text inline
  // nodes by default. Represented as a `<pre>` element with a
  // `<code>` element inside of it.
  code_block: {
    content: 'text*',
    marks: '',
    group: 'block',
    code: true,
    defining: true,
    parseDOM: [{tag: 'pre', preserveWhitespace: 'full'}],
    toDOM() { return Nodelements.pre }
  },

  // :: NodeSpec The text node.
  text: {
    group: 'inline'
  },

  // :: NodeSpec An inline image (`<img>`) node. Supports `src`,
  // `alt`, and `href` attributes. The latter two default to the empty
  // string.
  image: {
    inline: true,
    attrs: {
      src: {},
      alt: {default: null},
      title: {default: null}
    },
    group: 'inline',
    draggable: true,
    parseDOM: [{tag: 'img[src]', getAttrs(dom) {
      return {
        src: dom.getAttribute('src'),
        title: dom.getAttribute('title'),
        alt: dom.getAttribute('alt')
      }
    }}],
    toDOM(node) {
      let {src, alt, title} = node.attrs;
      return ['img', {src, alt, title}]
    }
  },

  // :: NodeSpec A hard line break, represented in the DOM as `<br>`.
  hard_break: {
    inline: true,
    group: 'inline',
    selectable: false,
    parseDOM: [{tag: 'br'}],
    toDOM() { return Nodelements.br }
  },

  check_list: {
    content: 'check_list_item+',
    group: 'block',
    parseDOM: [{ tag: 'ul[data-type="checklist"]' }],
    toDOM() { return ['ul', { 'data-type': 'checklist', class: 'check-list' }, 0]; },
  },

  check_list_item: {
    content: 'paragraph block*',
    attrs: { checked: { default: false } },
    defining: true,
    parseDOM: [{
      tag: 'li[data-type="check-item"]',
      getAttrs(dom) {
        return { checked: dom.getAttribute('data-checked') === 'true' };
      },
    }],
    toDOM(node) {
      const { checked } = node.attrs;
      return ['li', {
        'data-type': 'check-item',
        'data-checked': checked ? 'true' : 'false',
        class: `check-list-item${checked ? ' checked' : ''}`,
      }, 0];
    },
  },

  checkbox: {
    inline: true,
    group: 'inline',
    selectable: true,
    draggable: true,
    attrs: {
      checked: {default: false},
      classes: { default: 'todo-checkbox' },
      type: {default: 'checkbox'},
      style: { default: 'margin-right: .4em;' },
    },
    parseDOM: [{
      tag: 'input[type=checkbox]',
      getAttrs(dom) {
        return {
          style: dom.getAttribute('style'),
          type: dom.getAttribute('type'),
          checked: dom.getAttribute('checked'),
          classes: dom.getAttribute('class'),
        };
      }
    },],
    toDOM(node) {
      let { checked, type, style, classes } = node.attrs;
      if (checked !== true) {
        checked = null;
      }
      return ['input', { checked, type, style, class: classes }];
    }
  },

  // :: NodeSpec a div attribute, represented in the DOM as `<div>`.
  div: {
    inline: true,
    group: 'block+',
    selectable: false,
    attrs: {
      align: {default: null},
      autofocus: {default: null},
      contentEditable: {default: null},
      dir: {default: null},
      tabIndex: {default: '-1'},
      title: {default: null},
      style: {default: null},
      class: {default: null},
    },
    parseDOM: [
      {
        tag: 'div',
        getAttrs(dom) {
          return {
            align: dom.getAttribute('align'),
            autofocus: dom.getAttribute('autofocus'),
            contentEditable: dom.getAttribute('contentEditable'),
            dir: dom.getAttribute('dir'),
            tabIndex: dom.getAttribute('tabIndex'),
            title: dom.getAttribute('title'),
            style: dom.getAttribute('style'),
            class: dom.getAttribute('class'),
          };
        }
      },
    ],
    toDOM(node) {
      return ['div', {...node.attrs}, 0];
    }
  },

  svg: {
    inline: true,
    group: 'block+',
    selectable: false,
    attrs: {
      height: {default: null},
      class: {default: null},
      viewBox: {default: null},
      version: {default: null},
      width: {default: '-1'},
      style: {default: null},
      xmlns: {default: null},
    },
    parseDOM: [
      {
        tag: 'svg',
        getAttrs(dom) {
          return {
            height: dom.getAttribute('height'),
            class: dom.getAttribute('class'),
            viewBox: dom.getAttribute('viewBox'),
            version: dom.getAttribute('version'),
            width: dom.getAttribute('width'),
            style: dom.getAttribute('style'),
            xmlns: dom.getAttribute('xmlns'),
          };
        }
      },
    ],
    toDOM(node) {
      return ['svg', {...node.attrs}, 0];
    }
  },

  path: {
    inline: true,
    group: 'block+',
    selectable: false,
    attrs: {
      fill_rule: {default: null},
      d: {default: null},
      viewBox: {default: null},
      version: {default: null},
      width: {default: '-1'},
      aria_hidden: {default: null},
      style: {default: null},
    },
    parseDOM: [
      {
        tag: 'path',
        getAttrs(dom) {
          return {
            fill_rule: dom.getAttribute('fill-rule'),
            d: dom.getAttribute('d'),
            width: dom.getAttribute('width'),
            style: dom.getAttribute('style'),
          };
        }
      },
    ],
    toDOM(node) {
      return ['path', {'fill-rule': node.attrs.fill_rule, ...node.attrs}, 0];
    }
  },

  g: {
    inline: true,
    group: 'block+',
    selectable: false,
    attrs: {
      ['data-name']: {default: null},
      fill: {default: null},
    },
    parseDOM: [
      {
        tag: 'g',
        getAttrs(dom) {
          return {
            ['data-name']: dom.getAttribute('data-name'),
            fill: dom.getAttribute('fill'),
          };
        }
      },
    ],
    toDOM(node) {
      return ['path', {...node.attrs}, 0];
    }
  },

  html: {
    inline: true,
    group: 'block+',
    selectable: false,
    parseDOM: [{tag: 'html'}],
    toDOM() {
      return ['html', 0];
    }
  },

  base: {
    inline: true,
    group: 'block+',
    selectable: false,
    parseDOM: [{tag: 'base'}],
    toDOM() {
      return ['base', 0];
    }
  },

  link: {
    inline: true,
    group: 'block+',
    selectable: false,
    parseDOM: [{tag: 'link'}],
    toDOM() {
      return ['link', 0];
    }
  },

  style: {
    inline: true,
    group: 'block+',
    selectable: false,
    parseDOM: [{tag: 'style'}],
    toDOM() {
      return ['style', 0];
    }
  },

  title: {
    inline: true,
    group: 'block+',
    selectable: false,
    parseDOM: [{tag: 'title'}],
    toDOM() {
      return ['title', 0];
    }
  },

  body: {
    inline: true,
    group: 'block+',
    selectable: false,
    parseDOM: [{tag: 'body'}],
    toDOM() {
      return ['body', 0];
    }
  },

  // table: {
  //   inline: true,
  //   group: 'block+',
  //   selectable: false,
  //   parseDOM: [{tag: 'table'}],
  //   toDOM() {
  //     return ['table', 0];
  //   }
  // },

  // tbody: {
  //   inline: true,
  //   group: 'block+',
  //   selectable: false,
  //   parseDOM: [{tag: 'tbody'}],
  //   toDOM() {
  //     return ['tbody', 0];
  //   }
  // },

  // td: {
  //   inline: true,
  //   group: 'block+',
  //   selectable: false,
  //   parseDOM: [{tag: 'td'}],
  //   toDOM() {
  //     return ['td', 0];
  //   }
  // },

  // tfoot: {
  //   inline: true,
  //   group: 'block+',
  //   selectable: false,
  //   parseDOM: [{tag: 'tfoot'}],
  //   toDOM() {
  //     return ['tfoot', 0];
  //   }
  // },

  // th: {
  //   inline: true,
  //   group: 'block+',
  //   selectable: false,
  //   parseDOM: [{tag: 'th'}],
  //   toDOM() {
  //     return ['th', 0];
  //   }
  // },

  // thead: {
  //   inline: true,
  //   group: 'block+',
  //   selectable: false,
  //   parseDOM: [{tag: 'thead'}],
  //   toDOM() {
  //     return ['thead', 0];
  //   }
  // },

  // tr: {
  //   inline: true,
  //   group: 'block+',
  //   selectable: false,
  //   parseDOM: [{tag: 'tr'}],
  //   toDOM() {
  //     return ['tr', 0];
  //   }
  // },

  button: {
    inline: true,
    group: 'block+',
    selectable: false,
    parseDOM: [{tag: 'button'}],
    toDOM() {
      return ['button', 0];
    }
  },

  textarea: {
    inline: true,
    group: 'block+',
    selectable: false,
    parseDOM: [{tag: 'textarea'}],
    toDOM() {
      return ['textarea', 0];
    }
  },
  ...tableNodesSpec,
// [x] html
// [x] base
// [x] link
// [x] style
// [x] title
// [x] body
// [] address
// [] article
// [] aside
// [] footer
// [] header
// [x] h1
// [x] h2
// [x] h3
// [x] h4
// [x] h5
// [x] h6
// [] hgroup
// [] main
// [] nav
// [] section
// [] blockquote
// [] dd
// [x] div
// [] dl
// [] dt
// [] figcaption
// [] figure
// [x] hr
// [x] li
// [x] ol
// [x] p
// [] pre
// [x] ul
// [] area
// [] audio
// [x] img
// [] map
// [] track
// [] video
// [] embed
// [] iframe
// [] object
// [] param
// [] picture
// [] source
// [] canvas
// [] noscript
// [] script
// [] del
// [] ins
// [] caption
// [] col
// [] colgroup
// [x] table
// [x] tbody
// [x] td
// [x] tfoot
// [x] th
// [x] thead
// [x] tr
// [x] button
// [] datalist
// [] fieldset
// [] form
// [] input
// [] label
// [] legend
// [] meter
// [] optgroup
// [] option
// [] output
// [] progress
// [] select
// [] textarea
// [] details
// [] dialog
// [] menu
// [] summary
// [] slot
// [] template



// ===== deprecated
// acronym
// applet
// basefont
// bgsound
// big
// blink
// center
// command
// content
// dir
// element
// font
// frame
// frameset
// image
// isindex
// keygen
// listing
// marquee
// menuitem
// multicol
// nextid
// nobr
// noembed
// noframes
// plaintext
// shawdow
// spacer
// strike
// tt
// xmp
};

export default nodes;